// Çalışan çıkış (işten ayrılış) işlemleri.
//
// Çıkış iki yerden tetiklenir:
//   1. Çalışan Yönetimi › "Durum Değiştir" ile çalışan inaktife alınırken,
//   2. Çalışanın dosyalarına "İşten Ayrılış Sözleşme" yüklendiğinde.
// Her ikisi de aynı işi yapar; tek fark tarihi soran pencerenin nereden
// açıldığıdır. Tarih her durumda elle girilir.

import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebaseConfig';

/** Yüklendiğinde çıkış sürecini başlatan dosya türü. */
export const EXIT_CONTRACT_CATEGORY = 'İşten Ayrılış Sözleşme';

/**
 * Çalışanı atandığı tüm projelerde hedef duruma geçirir.
 * İlişki iki taraflı tutulduğu için hem Projects/{id}.employees hem de
 * Employees/{id}.projects güncellenir.
 */
export const updateEmployeeStatusInProjects = async (employeeId, targetStatus = 'passive') => {
  const employeeRef = doc(db, 'Employees', employeeId);
  const employeeDoc = await getDoc(employeeRef);
  if (!employeeDoc.exists()) return;

  const projects = employeeDoc.data().projects || [];
  const updatePromises = [];
  const uniqueProjectIds = [...new Set(projects.map((p) => p.id).filter(Boolean))];

  for (const projectId of uniqueProjectIds) {
    const projectRef = doc(db, 'Projects', projectId);
    // eslint-disable-next-line no-await-in-loop
    const projectDoc = await getDoc(projectRef);
    if (!projectDoc.exists()) continue;

    const projectEmployees = projectDoc.data().employees || [];
    const hasChanges = projectEmployees.some(
      (emp) => emp.id === employeeId && emp.status !== targetStatus
    );
    if (!hasChanges) continue;

    const updatedEmployees = projectEmployees.map((emp) =>
      emp.id === employeeId ? { ...emp, status: targetStatus } : emp
    );
    updatePromises.push(updateDoc(projectRef, { employees: updatedEmployees }));
  }

  const updatedProjects = projects.map((proj) => ({ ...proj, status: targetStatus }));
  updatePromises.push(updateDoc(employeeRef, { projects: updatedProjects }));

  await Promise.all(updatePromises);
};

/**
 * Çalışanın çıkışını tamamlar:
 *   - tüm proje atamaları pasife düşer,
 *   - Active = 0 olur; giriş ekranı Active !== 1 olanları "Giriş Yetkiniz
 *     Kaldırılmıştır." diyerek geri çevirdiği için sisteme giriş kapanır
 *     (bkz. LoginPage),
 *   - işten ayrılma tarihi kaydedilir.
 *
 * @param {string} employeeId Çalışan doküman kimliği
 * @param {string} exitDate   'yyyy-mm-dd' biçiminde işten ayrılma tarihi
 */
export const deactivateEmployeeForExit = async (employeeId, exitDate) => {
  if (!employeeId) throw new Error('Çalışan kimliği bulunamadı');
  if (!exitDate) throw new Error('İşten ayrılma tarihi gerekli');

  // Önce projeler: çıkış yazıldıktan sonra hata olursa çalışan hem aktif
  // görünüp hem projelerde pasif kalmasın.
  await updateEmployeeStatusInProjects(employeeId, 'passive');

  await updateDoc(doc(db, 'Employees', employeeId), {
    Active: 0,
    employeeExitDate: exitDate,
  });
};

/** Çalışan hâlâ aktif mi? (Çıkış akışı yalnızca aktif çalışan için anlamlı) */
export const isActiveEmployee = (employee) => Number(employee?.Active) === 1;

/** Bugünün tarihi, <input type="date"> için yerel saat dilimine göre. */
export const todayDateInput = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};
